import { useMutation } from "@apollo/client";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Refresh from "@mui/icons-material/Refresh";
import { Button, IconButton, Stack, Tooltip } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { BigTooltip } from "components/BigTooltip";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  ACCESS_OBSERVATION_WINDOW_START,
  noAccessObservationLabel,
} from "encamp-shared/src/waste/siteManagerAccess";
import { gql } from "generated-graphql";
import {
  QueueEManifestSiteAccessJobMutation,
  SiteManagerAccessForStaffQuery,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";
import {
  SITE_MANAGER_ACCESS_FILTER_VALUES,
  SITE_MANAGER_ACCESS_TOOLTIP,
  siteManagerAccessToLabel,
} from "routes/Customer/Waste/siteManagerAccess";
import { prettyPrintDate } from "util/dates";
import { stateOptionLabel } from "util/filterOptionLabels";

import { SITE_ACCESS_REFRESH_TOOLTIP } from "../siteAccessJob";
import { QUEUE_E_MANIFEST_SITE_ACCESS_JOB, SITE_MANAGER_ACCESS_FOR_STAFF_QUERY } from "./api";

type Row = SiteManagerAccessForStaffQuery["siteManagerAccessForStaff"]["items"][number];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Opens on the gap without hiding anything: hasSiteAccess sorts false before
 * true, so no-access lands at the top and granted follows, with the tiebreaker
 * chain grouping each block by tenant. Preferred over landing pre-filtered —
 * nothing is hidden, and the tab behaves like every other tab.
 */
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "siteManagerAccess", sort: "asc" }];

// Tenant is a column but needs its own filter key: the resolver answers to
// `tenantId` while the column renders the name.
const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  { key: "tenantId", header: "Tenant", filterKeyType: "multipleTenant" },
];

// Columns become filter keys automatically and the resolver drops keys it doesn't
// answer, so a column without a server key would render a filter that does
// nothing. These three have none.
const EXCLUDE_FILTER_COLUMNS = [
  "tenantName",
  "accessLastObservedAt",
  "accessLastCheckedAt",
  "reportedWasteGeneratorStatus",
  "calculatedWasteGeneratorStatus",
];

const columns: OmnisearchGridColDef<Row>[] = [
  {
    field: "tenantName",
    headerName: "Tenant",
    flex: 1,
    sortable: true,
  },
  {
    field: "facilityName",
    headerName: "Facility",
    flex: 1.2,
    sortable: true,
  },
  {
    field: "epaId",
    headerName: "EPA ID",
    flex: 0.7,
    sortable: true,
    // Null means the facility has no EPA ID on file at all — a different gap from
    // having one we hold no access for, and one you can't request access for.
    renderCell: ({ row }) => row.epaId ?? "No EPA ID",
  },
  {
    field: "state",
    headerName: "State",
    flex: 0.3,
    sortable: true,
    // Same picker the customer waste facilities grid uses, so the two read alike.
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(STATE_NAMES),
    enumPresentationFunction: stateOptionLabel,
  },
  {
    field: "siteManagerAccess",
    headerName: "Site Manager Access",
    flex: 0.6,
    // Measured: 210 is where this label stops wrapping once the hover sort and
    // menu icons claim their ~50px. Without a minWidth the column shrinks until
    // the label fits on one line at rest and wraps only on hover, which reads as
    // the header bouncing.
    minWidth: 210,
    sortable: true,
    headerClassName: "wrapHeader",
    filterKeyType: "enumAutocomplete",
    enumValues: SITE_MANAGER_ACCESS_FILTER_VALUES,
    enumPresentationFunction: siteManagerAccessToLabel,
    valueGetter: (_, row) => siteManagerAccessToLabel(row.siteManagerAccess),
    renderCell: ({ value }) => <Tooltip title={SITE_MANAGER_ACCESS_TOOLTIP}>{value}</Tooltip>,
  },
  {
    field: "reportedWasteGeneratorStatus",
    headerName: "Reported Status",
    flex: 0.6,
    minWidth: 150,
    sortable: true,
    headerClassName: "wrapHeader",
    renderCell: ({ row }) =>
      row.reportedWasteGeneratorStatus
        ? prettyPrintEnumValue(row.reportedWasteGeneratorStatus)
        : "—",
  },
  {
    field: "calculatedWasteGeneratorStatus",
    headerName: "Calculated Status",
    flex: 0.6,
    minWidth: 150,
    sortable: true,
    headerClassName: "wrapHeader",
    renderCell: ({ row }) =>
      row.calculatedWasteGeneratorStatus
        ? prettyPrintEnumValue(row.calculatedWasteGeneratorStatus)
        : "—",
  },
  {
    field: "accessLastObservedAt",
    headerName: "Access Last Held",
    flex: 0.5,
    minWidth: 150,
    // From a separate lookup off eManifestSiteSync, so there is nothing to order on.
    sortable: false,
    headerClassName: "wrapHeader",
    // The distinction the gap review turns on: a date means we held access and
    // lost it (a regression to raise with the customer), no date means we have
    // no record of holding it (request access). Deliberately not labelled
    // "Never" -- the record only starts at ACCESS_OBSERVATION_WINDOW_START, so
    // access held and lost before then is indistinguishable from never having
    // had it, and "Never" would read as stronger evidence than we have.
    renderCell: ({ row }) => {
      if (row.accessLastObservedAt) return prettyPrintDate(row.accessLastObservedAt);
      return (
        <Tooltip
          title={`No run has observed Site Manager access for this EPA ID since ${ACCESS_OBSERVATION_WINDOW_START}, when the record begins. Access held and lost before then leaves no trace.`}
        >
          <span>{noAccessObservationLabel()}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "accessLastCheckedAt",
    headerName: "Access Last Checked",
    flex: 0.5,
    minWidth: 150,
    sortable: false,
    headerClassName: "wrapHeader",
    renderCell: ({ row }) =>
      row.accessLastCheckedAt ? prettyPrintDate(row.accessLastCheckedAt) : "—",
  },
];

const getItems = (data: SiteManagerAccessForStaffQuery) => data.siteManagerAccessForStaff.items;
const getCount = (data: SiteManagerAccessForStaffQuery) => data.siteManagerAccessForStaff.count;

const buildVariables = (
  omnisearch: string,
  pagination: { page: number; pageSize: number },
  sort: GridSortModel
) => ({
  search: omnisearch,
  page: pagination.page,
  pageSize: pagination.pageSize,
  sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
});

export function SiteManagerAccess() {
  const alerts = useAlerts();
  useBreadcrumb({ label: "RCRAInfo Site Access" });

  const [queueJob, { loading: isQueueing }] = useMutation<QueueEManifestSiteAccessJobMutation>(
    QUEUE_E_MANIFEST_SITE_ACCESS_JOB,
    {
      onCompleted: (data) => {
        const jobId = data.queueEManifestSiteAccessJob.id;
        alerts.success(`Site access job ${jobId} queued. Access states refresh when it finishes.`);
      },
      onError: (error) => {
        alerts.error("Failed to queue site access job", error);
      },
    }
  );

  const handleRefresh = useCallback(() => {
    queueJob();
  }, [queueJob]);

  const commandButtons = useMemo(
    () => [
      <Stack key="refresh" direction="row" spacing={1} alignItems="center">
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={isQueueing}
        >
          {isQueueing ? "Queueing…" : "Re-check access"}
        </Button>
        <BigTooltip title={SITE_ACCESS_REFRESH_TOOLTIP}>
          <IconButton size="small">
            <InfoOutlined color="disabled" />
          </IconButton>
        </BigTooltip>
      </Stack>,
    ],
    [handleRefresh, isQueueing]
  );

  return (
    <OmnisearchDataGridV2<SiteManagerAccessForStaffQuery, ReturnType<typeof buildVariables>, Row>
      query={SITE_MANAGER_ACCESS_FOR_STAFF_QUERY}
      getItems={getItems}
      getCount={getCount}
      buildVariables={buildVariables}
      columns={columns}
      commandButtons={commandButtons}
      persistenceKey="staff-site-manager-access"
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      initialSortModel={INITIAL_SORT_MODEL}
      additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
      excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
      noRowsMessage="No waste-enabled facilities match these filters."
    />
  );
}
